import debug from 'debug';
import jwt_decode from 'jwt-decode';
import tokenStorage from '../utils/tokenStorage';
import {
  validateTokenStructure,
  validateDecodedToken,
  TokenValidationError
} from '../utils/tokenValidator';
import { secureClearTokenCache } from '../utils/secureMemory';
import { serverLogger } from '../libs/datadog-logger/loggers/serverLogger';
import { hasUsableRefreshToken } from './refreshTokenUtils';

// Debug namespaces for session management
const debugSession = debug('impl:step6:session');
const debugCache = debug('impl:step11:cache');
const debugError = debug('impl:step6:session:error');

// ✅ STEP 11: JWT decode cache to reduce CPU overhead
let tokenCache = {
  token: null,
  decodedToken: null,
  refreshToken: null,
  authData: null,
};

const getRefreshExpSeconds = (refreshExpClaim, fallbackExp) => {
  if (refreshExpClaim === null || refreshExpClaim === undefined || refreshExpClaim === '') {
    return fallbackExp;
  }

  const parsedRefreshExp = Number(refreshExpClaim);
  return Number.isFinite(parsedRefreshExp) ? parsedRefreshExp : fallbackExp;
};

export const setAuthData = (token, userName, refreshToken, userImpersonation) => {
  debugSession('step 6: starting setAuthData', {
    userName,
    userImpersonation,
    tokenLength: token?.length,
    refreshTokenLength: refreshToken?.length,
    tokenType: typeof token,
    refreshTokenType: typeof refreshToken,
    hasToken: !!token,
    hasRefreshToken: !!refreshToken
  });

  try {
    // ✅ STEP 1: Validate JWT structure before decoding
    debugSession('step 6.1: validating JWT structures');
    debugSession('step 6.1: validating access token structure (length: %d)', token?.length || 0);
    try {
      validateTokenStructure(token);
      debugSession('step 6.1: access token validation passed');
    } catch (error) {
      debugError('step 6.1: access token validation FAILED', { error: error.message });
      throw new TokenValidationError(`Invalid access token: ${error.message}`, 'token');
    }

    // Refresh token validation: Only check it's a non-empty string (not a JWT in our system)
    debugSession('step 6.1: validating refresh token (length: %d)', refreshToken?.length || 0);
    if (!refreshToken || typeof refreshToken !== 'string' || refreshToken.trim().length === 0) {
      debugError('step 6.1: refresh token validation FAILED - not a valid string');
      throw new TokenValidationError('Refresh token must be a non-empty string', 'refreshToken');
    }
    debugSession('step 6.1: refresh token validation passed (simple string token)');

    // ✅ STEP 2: Decode access token (refresh token is not a JWT, so we don't decode it)
    debugSession('step 6.2: decoding access token JWT');
    const decodedToken = jwt_decode(token);
    debugSession('step 6.2: access token decoded successfully', { 
      userId: decodedToken.Id,
      exp: decodedToken.exp,
      refreshExp: decodedToken.refresh_exp 
    });

    // ✅ STEP 3: Validate decoded claims
    debugSession('step 6.3: validating decoded token claims');
    validateDecodedToken(decodedToken);
    debugSession('step 6.3: claim validation passed for access token');

    // ✅ STEP 4: Build auth data object
    const dateExp = new Date(0);
    dateExp.setUTCSeconds(decodedToken.exp);

    const refreshExpSeconds = getRefreshExpSeconds(decodedToken.refresh_exp, decodedToken.exp);
    const canRefresh = hasUsableRefreshToken(refreshToken);
    const authData = {
      token,
      userName: userName || decodedToken.UserName,
      refreshToken, // Simple string token, not a JWT
      userImpersonation,
      exp: dateExp,
      created: new Date(new Date(0).setUTCSeconds(decodedToken.nbf)),
      // Prefer refresh_exp, fallback to exp for legacy tokens.
      // Gate on canRefresh so a non-usable refresh token (e.g. 'not-available')
      // never persists a positive refresh deadline — symmetric with setAuthDataFromUrl.
      refresh: canRefresh && refreshExpSeconds
        ? new Date(new Date(0).setUTCSeconds(refreshExpSeconds))
        : null,
      canRefresh,
      userId: decodedToken.Id,
    };

    debugSession('step 6.4: auth data object built', { 
      userId: authData.userId,
      userName: authData.userName,
      exp: authData.exp,
      userImpersonation: authData.userImpersonation 
    });

    // ✅ STEP 5: Save validated data
    debugSession('step 6.5: saving validated auth data to storage');
    tokenStorage.save(authData);
    debugSession('step 6.5: auth data saved successfully');

    // ✅ STEP 11: Update cache after successful validation
    debugCache('step 11: updating JWT decode cache', { 
      userId: decodedToken.Id,
      tokenExp: decodedToken.exp 
    });
    tokenCache = {
      token,
      decodedToken,
      refreshToken,
      authData,
    };
    debugCache('step 11: cache updated successfully');

    console.info('[Auth] Tokens validated and saved successfully');

  } catch (error) {
    debugError('step 6: token validation failed', { 
      errorName: error.name,
      errorMessage: error.message,
      field: error instanceof TokenValidationError ? error.field : 'unknown',
      userName 
    });
    console.error('[Auth] Token validation failed:', error);

    // Clear any invalid tokens
    debugSession('step 6: clearing invalid tokens from storage');
    tokenStorage.remove();

    // ✅ STEP 11: Clear cache on validation error
    // 🔒 Security: Securely overwrite tokens in memory before clearing
    debugCache('step 11: securely clearing cache due to validation error');
    tokenCache = secureClearTokenCache(tokenCache);

    // Track validation failures in Datadog
    debugSession('step 6: tracking validation failure in Datadog');
    serverLogger.error('auth.token_validation_failed', {
      source: 'token_validation',
      type: error instanceof TokenValidationError ? error.field : 'unknown',
      errorMessage: error.message,
    });

    // Re-throw with user-friendly message
    const message = error instanceof TokenValidationError
      ? `Invalid authentication token: ${error.message}`
      : 'Invalid authentication tokens received. Please log in again.';

    debugError('step 6: re-throwing with user-friendly message', { message });
    throw new Error(message);
  }
};

export const setAuthDataFromUrl = (token) => {
  debugSession('step 6: starting setAuthDataFromUrl', { tokenLength: token?.length });
  
  let decodedToken = null;
  try {
    // ✅ Add validation
    debugSession('step 6: validating token from URL');
    validateTokenStructure(token);
    decodedToken = jwt_decode(token);
    validateDecodedToken(decodedToken);
    debugSession('step 6: URL token validated successfully', { 
      userId: decodedToken.Id,
      userName: decodedToken.UserName 
    });
  } catch (error) {
    debugError('step 6: URL token validation failed', { 
      errorMessage: error.message,
      errorType: error.name 
    });
    console.error('[Auth] Token validation failed', error);
    tokenStorage.remove();
    throw error;
  }

  const dateExp = new Date(0);
  dateExp.setUTCSeconds(decodedToken.exp);

  const refreshExp = decodedToken.refresh_exp
    ? new Date(0).setUTCSeconds(Number(decodedToken.refresh_exp))
    : new Date(Date.now() + 30 * 60 * 1000);
  const refreshToken = decodedToken.refresh_token ?? null;
  const canRefresh = hasUsableRefreshToken(refreshToken);

  const createdDate = decodedToken.nbf
    ? new Date(0).setUTCSeconds(decodedToken.nbf)
    : new Date();

  const authData = {
    token,
    userName: decodedToken.UserName,
    userImpersonation: false,
    refreshToken,
    exp: dateExp,
    created: createdDate,
    refresh: canRefresh ? new Date(refreshExp) : null,
    canRefresh,
    autoLogin: true,
  };

  debugSession('step 6: saving URL auth data', { 
    userName: authData.userName,
    autoLogin: true,
    hasRefreshToken: !!authData.refreshToken,
  });
  tokenStorage.save(authData);
  debugSession('step 6: URL auth data saved successfully');
  
  return decodedToken;
};

export const getAuthData = () => {
  debugSession('step 11: starting getAuthData');
  
  try {
    const authData = tokenStorage.get();

    // No data stored
    if (authData === null || authData.length === 0) {
      debugSession('step 11: no auth data in storage');
      return null;
    }

    // Parse stored JSON
    debugSession('step 11: parsing stored auth data');
    const authDataObj = JSON.parse(authData);

    // ✅ STEP 11: Check cache first
    const isCacheHit = tokenCache.token === authDataObj.token && tokenCache.decodedToken;
    debugCache('step 11: checking cache', { 
      cacheHit: isCacheHit,
      hasCachedToken: !!tokenCache.token,
      hasStoredToken: !!authDataObj.token 
    });

    if (isCacheHit) {
      // Cache hit - check if token is still valid (not expired)
      const now = Math.floor(Date.now() / 1000); // Current time in seconds
      const accessExp = tokenCache.decodedToken.exp;
      const timeToExpiry = accessExp - now;

      debugCache('step 11: cache hit - checking expiration', { 
        exp: tokenCache.decodedToken.exp,
        now,
        timeToExpiry,
        isValid: timeToExpiry > 0 
      });

      // ✅ FIX: Check REFRESH token expiration, not access token expiration
      // For auto-login tokens (URL-based), only check main exp, ignore refresh_exp
      const isAutoLogin = authDataObj.autoLogin === true;
      const refreshExp = getRefreshExpSeconds(tokenCache.decodedToken.refresh_exp, accessExp);
      const refreshTimeToExpiry = refreshExp ? refreshExp - now : -1;
      const accessTokenExpired = accessExp && accessExp <= now;
      const refreshTokenExpired = isAutoLogin ? false : (refreshExp ? refreshExp <= now : accessTokenExpired);

      // Debug logging for refresh token expiration
      debugCache('step 11: refresh token expiration check (cache hit)', {
        isAutoLogin: isAutoLogin,
        refreshExpUTC: refreshExp ? new Date(refreshExp * 1000).toISOString() : 'N/A',
        currentTimeUTC: new Date(now * 1000).toISOString(),
        accessExpUTC: accessExp ? new Date(accessExp * 1000).toISOString() : 'N/A',
        refreshExp: refreshExp,
        accessExp: accessExp,
        now: now,
        refreshTimeToExpiry: refreshTimeToExpiry,
        accessTimeToExpiry: timeToExpiry,
        refreshTokenExpired: refreshTokenExpired,
        accessTokenExpired: accessTokenExpired
      });

      debugCache('step 11: cache hit - checking token expiration', {
        isAutoLogin,
        accessTokenExp: tokenCache.decodedToken.exp,
        refreshTokenExp: refreshExp,
        now,
        accessTokenExpired,
        refreshTokenExpired,
        accessTimeToExpiry: timeToExpiry,
        refreshTimeToExpiry
      });

      if (refreshTokenExpired) {
        // Refresh token expired - clear everything and force re-login
        debugCache('step 11: cached REFRESH token expired, clearing all tokens', {
          refreshExp,
          expiredBy: now - refreshExp
        });

        // 🔒 Security: Securely overwrite expired tokens in memory
        tokenCache = secureClearTokenCache(tokenCache);

        // Clear expired tokens from storage
        tokenStorage.remove();

        console.warn('[Auth] Refresh token expired, cleared from storage');
        return null;
      }

      // Access token expired but refresh token still valid - allow refresh to proceed
      if (accessTokenExpired) {
        debugCache('step 11: access token expired but refresh token valid - allowing refresh', {
          accessExp: tokenCache.decodedToken.exp,
          refreshExp,
          accessExpiredBy: now - tokenCache.decodedToken.exp,
          refreshTimeRemaining: refreshTimeToExpiry
        });
        console.info('[Auth] Access token expired, but refresh token valid - allowing token refresh');
      }

      // Return cached auth object (even if access token expired, as long as refresh token is valid)
      debugCache('step 11: returning cached auth object', {
        userId: tokenCache.decodedToken.Id,
        accessTokenExpired,
        refreshTokenValid: !refreshTokenExpired
      });

      const cachedCanRefresh = hasUsableRefreshToken(authDataObj.refreshToken);
      const cachedAuthObject = {
        unique_name:
          tokenCache.decodedToken.unique_name ||
          tokenCache.decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
        userId:
          tokenCache.decodedToken.nameid ||
          tokenCache.decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
        firstName: tokenCache.decodedToken.FirstName,
        lastName: tokenCache.decodedToken.LastName,
        IsAutoGenerated: tokenCache.decodedToken.IsAutoGenerated,
        samAccountName: tokenCache.decodedToken.SamAccountName,
        roles:
          tokenCache.decodedToken.role ||
          tokenCache.decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
        token: authDataObj.token,
        refreshToken: authDataObj.refreshToken,
        userImpersonation: authDataObj.userImpersonation,
        exp: new Date(0).setUTCSeconds(tokenCache.decodedToken.exp).valueOf(),
        created: new Date(0).setUTCSeconds(tokenCache.decodedToken.nbf).valueOf(),
        refresh: cachedCanRefresh
          ? new Date(0).setUTCSeconds(refreshExp ?? tokenCache.decodedToken.exp).valueOf()
          : null,
        canRefresh: cachedCanRefresh,
        autoLogin: authDataObj.autoLogin === true,
      };

      return cachedAuthObject;
    }

    // Cache miss or token changed - decode and validate
    debugCache('step 11: cache miss - decoding token', { 
      reason: tokenCache.token ? 'token changed' : 'no cached token' 
    });

    // ✅ Validate stored token structure
    debugSession('step 6: validating stored token structure');
    validateTokenStructure(authDataObj.token);

    debugSession('step 6: decoding stored token');
    const decodedToken = jwt_decode(authDataObj.token);

    // ✅ STEP 11: Check expiration immediately after decode
    const now = Math.floor(Date.now() / 1000);
    const accessExp = decodedToken.exp;
    const timeToExpiry = accessExp - now;

    // ✅ FIX: Check REFRESH token expiration, not access token expiration
    // For auto-login tokens (URL-based), only check main exp, ignore refresh_exp
    const isAutoLogin = authDataObj.autoLogin === true;
    const refreshExp = getRefreshExpSeconds(decodedToken.refresh_exp, accessExp);
    const refreshTimeToExpiry = refreshExp ? refreshExp - now : -1;
    const accessTokenExpired = accessExp && accessExp <= now;
    const refreshTokenExpired = isAutoLogin ? false : (refreshExp ? refreshExp <= now : accessTokenExpired);

    // Debug logging for refresh token expiration
    debugCache('step 11: refresh token expiration check (cache miss)', {
      isAutoLogin: isAutoLogin,
      refreshExpUTC: refreshExp ? new Date(refreshExp * 1000).toISOString() : 'N/A',
      currentTimeUTC: new Date(now * 1000).toISOString(),
      accessExpUTC: accessExp ? new Date(accessExp * 1000).toISOString() : 'N/A',
      refreshExp: refreshExp,
      accessExp: accessExp,
      now: now,
      refreshTimeToExpiry: refreshTimeToExpiry,
      accessTimeToExpiry: timeToExpiry,
      refreshTokenExpired: refreshTokenExpired,
      accessTokenExpired: accessTokenExpired
    });

    debugCache('step 11: checking token expiration after decode', {
      isAutoLogin,
      accessTokenExp: decodedToken.exp,
      refreshTokenExp: refreshExp,
      now,
      accessTokenExpired,
      refreshTokenExpired,
      accessTimeToExpiry: timeToExpiry,
      refreshTimeToExpiry
    });

    if (refreshTokenExpired) {
      // Refresh token expired - clear everything and force re-login
      debugError('step 11: stored REFRESH token is expired, clearing all tokens', {
        refreshExp,
        expiredBy: now - refreshExp
      });

      tokenStorage.remove();

      // 🔒 Security: Securely overwrite expired tokens in memory
      tokenCache = secureClearTokenCache(tokenCache);

      console.warn('[Auth] Refresh token from storage is expired, cleared');
      return null;
    }

    // Access token expired but refresh token still valid - allow refresh to proceed
    if (accessTokenExpired) {
      debugCache('step 11: access token expired but refresh token valid - allowing refresh', {
        accessExp: decodedToken.exp,
        refreshExp,
        accessExpiredBy: now - decodedToken.exp,
        refreshTimeRemaining: refreshTimeToExpiry
      });
      console.info('[Auth] Access token expired, but refresh token valid - allowing token refresh');
    }

    debugSession('step 11: building auth object from decoded token', { 
      userId: decodedToken.Id,
      userName: decodedToken.UserName 
    });

    const canRefresh = hasUsableRefreshToken(authDataObj.refreshToken);
    const authObject = {
      unique_name:
        decodedToken.unique_name ||
        decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
      userId:
        decodedToken.nameid ||
        decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
      firstName: decodedToken.FirstName,
      lastName: decodedToken.LastName,
      IsAutoGenerated: decodedToken.IsAutoGenerated,
      samAccountName: decodedToken.SamAccountName,
      roles:
        decodedToken.role ||
        decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
      token: authDataObj.token,
      refreshToken: authDataObj.refreshToken,
      userImpersonation: authDataObj.userImpersonation,
      exp: new Date(0).setUTCSeconds(decodedToken.exp).valueOf(),
      created: new Date(0).setUTCSeconds(decodedToken.nbf).valueOf(),
      refresh: canRefresh
        ? new Date(0).setUTCSeconds(refreshExp ?? decodedToken.exp).valueOf()
        : null,
      canRefresh,
      autoLogin: authDataObj.autoLogin === true,
    };

    // ✅ STEP 11: Update cache with newly decoded token
    debugCache('step 11: updating cache with newly decoded token', { 
      userId: decodedToken.Id,
      timeToExpiry 
    });
    tokenCache = {
      token: authDataObj.token,
      decodedToken,
      refreshToken: authDataObj.refreshToken,
      authData: authObject,
    };
    debugCache('step 11: cache updated successfully');

    return authObject;

  } catch (error) {
    debugError('step 6/11: error reading stored auth data', { 
      errorName: error.name,
      errorMessage: error.message,
      isValidationError: error instanceof TokenValidationError,
      field: error instanceof TokenValidationError ? error.field : undefined 
    });
    console.error('[Auth] Stored auth data is invalid:', error);

    // Clear corrupted data
    debugSession('step 6: clearing corrupted storage');
    tokenStorage.remove();

    // ✅ STEP 11: Clear cache on error
    // 🔒 Security: Securely overwrite corrupted tokens in memory
    debugCache('step 11: securely clearing cache due to error');
    tokenCache = secureClearTokenCache(tokenCache);

    // Track storage corruption in Datadog
    debugSession('step 6: tracking storage corruption in Datadog');
    serverLogger.error('auth.storage_corrupted', {
      error: error.message,
      type: error instanceof TokenValidationError ? error.field : 'parse_error',
    });

    return null;
  }
};

export const autoLogin = (token) => {
  debugSession('step 6: starting autoLogin', { tokenLength: token?.length });
  tokenStorage.clear();

  const decodedToken = setAuthDataFromUrl(token);
  const userName =
    decodedToken.unique_name ||
    decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];

  debugSession('step 6: autoLogin completed', { userName });
  return userName;
};

/**
 * Securely clears the in-memory token cache.
 *
 * 🔒 Security: This function overwrites sensitive token data in memory with random
 * bytes before clearing references, mitigating token theft via memory dumps.
 *
 * Call this during logout or when clearing authentication state.
 *
 * @example
 * // During logout
 * clearTokenCache();
 * tokenStorage.clear();
 */
export const clearTokenCache = () => {
  debugCache('clearTokenCache: securely clearing token cache');
  tokenCache = secureClearTokenCache(tokenCache);
  debugCache('clearTokenCache: cache cleared successfully');
};
